import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { isOwnerOrAdmin } from '../utils/roleHelpers';
import api from '../services/api';
import './PaymentForm.css';

const PaymentForm = ({ initialData, onSubmit, onCancel, isLoading }) => {
  const { user } = useAuth();
  const isAdminOrOwner = isOwnerOrAdmin(user);
  
  // Función para formatear fechas sin problemas de zona horaria
  // Formato fecha para mostrar en tabla (DD.MM.YYYY)
  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return '—';
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [y, m, d] = dateValue.split('-');
      return `${d}.${m}.${y}`;
    }
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    // Si ya es string YYYY-MM-DD, devolverlo directamente (sin crear Date)
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    // Si es string ISO (con hora), extraer solo la parte de la fecha
    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      return dateValue.split('T')[0];
    }
    // Si es Date object, usar métodos locales (no UTC)
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Si es otro formato, intentar parsearlo
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    // Usar métodos locales para evitar conversión UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: initialData ? {
      ...initialData,
      payment_date: formatDateForInput(initialData.payment_date),
      due_date: formatDateForInput(initialData.due_date),
      paid_amount: initialData.paid_amount || 0,
      status: initialData.status || 'pending',
    } : {
      enrollment_id: '',
      payment_date: '',
      due_date: '',
      amount: '',
      paid_amount: '',
      payment_method: '',
      reference_number: '',
      notes: '',
      status: 'pending',
    },
  });

  const selectedEnrollmentId = watch('enrollment_id');
  const amount = watch('amount');
  const paidAmount = watch('paid_amount');
  const currentStatus = watch('status') || initialData?.status || 'pending';
  const [statusManuallyChanged, setStatusManuallyChanged] = useState(false);
  const [additionalPaymentAmount, setAdditionalPaymentAmount] = useState('');
  const [enrollmentSearchTerm, setEnrollmentSearchTerm] = useState('');
  const [debouncedEnrollmentSearch, setDebouncedEnrollmentSearch] = useState('');
  const [showEnrollmentDropdown, setShowEnrollmentDropdown] = useState(false);
  const enrollmentDropdownRef = useRef(null);
  const enrollmentSearchRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEnrollmentSearch(enrollmentSearchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [enrollmentSearchTerm]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        enrollmentDropdownRef.current &&
        !enrollmentDropdownRef.current.contains(event.target) &&
        enrollmentSearchRef.current &&
        !enrollmentSearchRef.current.contains(event.target)
      ) {
        setShowEnrollmentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Guardar el monto ya pagado original (no debe cambiar)
  const originalPaidAmount = React.useRef(initialData?.paid_amount || 0);
  
  // Actualizar la referencia cuando cambia initialData
  useEffect(() => {
    if (initialData?.paid_amount === undefined) return;
    const alreadyPaid = parseFloat(initialData.paid_amount) || 0;
    originalPaidAmount.current = alreadyPaid;
    const total = parseFloat(initialData.amount) || 0;
    const remaining = Math.max(0, Math.round((total - alreadyPaid) * 100) / 100);
    const collectable = ['pending', 'partial', 'overdue'].includes(initialData.status) && remaining > 0;
    if (collectable) {
      setAdditionalPaymentAmount(String(remaining));
      setValue('paid_amount', alreadyPaid + remaining);
      setValue('payment_date', new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Lima' }).format(new Date()));
      return;
    }
    setAdditionalPaymentAmount('');
    setValue('paid_amount', alreadyPaid);
  }, [initialData, setValue]);
  
  // Determinar qué campos están deshabilitados según el estado
  const getFieldRestrictions = (status) => {
    const restrictions = {
      paid: {
        disabledFields: ['amount', 'payment_date', 'payment_method', 'paid_amount', 'due_date'],
        editableFields: ['reference_number', 'invoice_number', 'notes'],
        statusChangeable: false,
        message: 'Este pago está pagado. Solo se pueden editar notas y números de referencia/factura.'
      },
      cancelled: {
        disabledFields: isAdminOrOwner ? [] : ['amount', 'payment_date', 'due_date', 'payment_method', 'paid_amount', 'reference_number', 'invoice_number', 'notes', 'status'],
        editableFields: isAdminOrOwner ? ['amount', 'payment_date', 'due_date', 'payment_method', 'paid_amount', 'reference_number', 'invoice_number', 'notes', 'status'] : [],
        statusChangeable: isAdminOrOwner,
        message: isAdminOrOwner ? 'Este pago está cancelado. Como administrador, puedes editarlo y reactivarlo.' : 'Este pago está cancelado. No se puede editar. Solo administradores pueden reactivarlo.'
      },
      pending: {
        disabledFields: [],
        editableFields: ['amount', 'payment_date', 'due_date', 'payment_method', 'paid_amount', 'reference_number', 'invoice_number', 'notes', 'status'],
        statusChangeable: true,
        message: null
      },
      partial: {
        disabledFields: ['amount', 'due_date'],
        editableFields: ['paid_amount', 'payment_method', 'reference_number', 'invoice_number', 'notes', 'status'],
        statusChangeable: true,
        message: 'Este pago está parcialmente pagado. No se puede editar el monto total ni la fecha de vencimiento.'
      },
      overdue: {
        disabledFields: ['amount', 'due_date'],
        editableFields: ['paid_amount', 'payment_method', 'reference_number', 'invoice_number', 'notes', 'status'],
        statusChangeable: true,
        message: 'Este pago está vencido. No se puede editar el monto total ni la fecha de vencimiento.'
      }
    };
    
    return restrictions[status] || restrictions.pending;
  };

  // El estado "paid" se calcula al vuelo mientras se escribe el abono.
  // Las restricciones siguen el estado con el que se abrió el pago, para no
  // bloquear método ni monto solo porque el abono ya completa la cuota.
  const openedStatus = initialData?.status || currentStatus;
  const collecting = Boolean(initialData) && ['pending', 'partial', 'overdue'].includes(openedStatus);
  const restrictions = getFieldRestrictions(collecting ? openedStatus : currentStatus);
  const additionalAmountLocked =
    initialData?.status === 'paid' ||
    (initialData?.status === 'cancelled' && !isAdminOrOwner);

  // Obtener matrículas (incluir "active" y "pending" para permitir crear pagos)
  const { data: enrollments } = useQuery('enrollments', async () => {
    const response = await api.get('/enrollments');
    // Filtrar solo matrículas activas o pendientes (no canceladas ni completadas)
    return response.data.filter(e => e.status === 'active' || e.status === 'pending');
  });

  // Obtener estudiantes para mostrar información
  const { data: students } = useQuery('students', async () => {
    const response = await api.get('/students');
    return response.data;
  });

  // Obtener grupos para mostrar información
  const { data: groups } = useQuery('groups', async () => {
    const response = await api.get('/groups');
    return response.data;
  });

  const { data: remoteEnrollmentMatches = [] } = useQuery(
    ['payment-form-enrollment-search', debouncedEnrollmentSearch],
    async () => {
      const studentsRes = await api.get('/students', {
        params: { search: debouncedEnrollmentSearch, limit: 15, is_active: true },
      });
      const found = studentsRes.data || [];
      if (!found.length) return [];
      const lists = await Promise.all(
        found.map((student) =>
          api.get('/enrollments', { params: { student_id: student.id, limit: 20 } }).then((response) =>
            (response.data || [])
              .filter((enrollment) => enrollment.status === 'active' || enrollment.status === 'pending')
              .map((enrollment) => ({
                ...enrollment,
                student: enrollment.student || student,
              }))
          )
        )
      );
      return lists.flat();
    },
    { enabled: !initialData && debouncedEnrollmentSearch.length > 0 }
  );

  const enrollmentOptions = debouncedEnrollmentSearch ? remoteEnrollmentMatches : (enrollments || []);

  // Matrícula seleccionada
  const selectedEnrollment = enrollmentOptions.find(e => e.id === parseInt(selectedEnrollmentId))
    || enrollments?.find(e => e.id === parseInt(selectedEnrollmentId));

  // Filtrar matrículas según término de búsqueda
  const filteredEnrollments = React.useMemo(() => {
    if (debouncedEnrollmentSearch) {
      return remoteEnrollmentMatches;
    }
    if (!enrollments || !enrollmentSearchTerm.trim()) {
      return enrollments || [];
    }
    
    const searchLower = enrollmentSearchTerm.toLowerCase();
    return enrollments.filter(enrollment => {
      const student = students?.find(s => s.id === enrollment.student_id);
      const group = groups?.find(g => g.id === enrollment.group_id);
      
      // Buscar por nombre del estudiante
      const studentName = student 
        ? `${student.first_name} ${student.last_name}`.toLowerCase()
        : '';
      
      // Buscar por documento del estudiante
      const documentNumber = student?.document_number?.toLowerCase() || '';
      
      // Buscar por nombre del grupo
      const groupName = group?.name?.toLowerCase() || '';
      
      return (
        studentName.includes(searchLower) ||
        documentNumber.includes(searchLower) ||
        groupName.includes(searchLower)
      );
    });
  }, [enrollments, enrollmentSearchTerm, debouncedEnrollmentSearch, remoteEnrollmentMatches, students, groups]);

  // Obtener información de la matrícula seleccionada para mostrar
  const selectedEnrollmentInfo = React.useMemo(() => {
    if (!selectedEnrollment) return null;
    const student = selectedEnrollment.student || students?.find(s => s.id === selectedEnrollment.student_id);
    const group = selectedEnrollment.group || groups?.find(g => g.id === selectedEnrollment.group_id);
    return {
      enrollment: selectedEnrollment,
      student,
      group
    };
  }, [selectedEnrollment, students, groups]);

  // Auto-completar monto desde la matrícula
  useEffect(() => {
    if (selectedEnrollment && !initialData && !amount) {
      setValue('amount', selectedEnrollment.final_monthly_fee);
    }
  }, [selectedEnrollment, setValue, initialData, amount]);

  // Actualizar el término de búsqueda cuando se selecciona una matrícula
  useEffect(() => {
    if (selectedEnrollmentId && !initialData && selectedEnrollmentInfo) {
      const student = selectedEnrollmentInfo.student;
      const group = selectedEnrollmentInfo.group;
      if (student && group) {
        setEnrollmentSearchTerm(`${student.first_name} ${student.last_name} - ${group.name}`);
      }
    }
  }, [selectedEnrollmentId, selectedEnrollmentInfo, initialData]);

  // Calcular monto pendiente usando el monto original ya pagado + el adicional
  const remainingAmount = React.useMemo(() => {
    if (!amount) return 0;
    const amt = parseFloat(amount) || 0;
    const originalPaid = originalPaidAmount.current || 0;
    const additional = parseFloat(additionalPaymentAmount) || 0;
    return Math.max(0, amt - (originalPaid + additional));
  }, [amount, additionalPaymentAmount]);

  // Monto total pagado (monto original ya pagado + el adicional)
  const totalPaidAmount = React.useMemo(() => {
    const originalPaid = originalPaidAmount.current || 0;
    const additional = parseFloat(additionalPaymentAmount) || 0;
    return originalPaid + additional;
  }, [additionalPaymentAmount]);

  // Función para calcular el estado automáticamente según el monto pagado
  const calculateStatusFromPaidAmount = React.useCallback((paid, total) => {
    const paidValue = parseFloat(paid) || 0;
    const totalValue = parseFloat(total) || 0;
    
    if (paidValue >= totalValue && totalValue > 0) {
      return 'paid';
    } else if (paidValue > 0 && paidValue < totalValue) {
      return 'partial';
    } else {
      return 'pending';
    }
  }, []);

  // Calcular el monto pendiente máximo permitido
  const maxAdditionalAmount = React.useMemo(() => {
    if (!amount) return null;
    const total = parseFloat(amount) || 0;
    return total - originalPaidAmount.current;
  }, [amount]);

  // Actualizar estado automáticamente cuando cambia el monto pagado (incluyendo el adicional)
  useEffect(() => {
    if (initialData && !statusManuallyChanged) {
      const autoStatus = calculateStatusFromPaidAmount(totalPaidAmount, amount);
      if (autoStatus !== currentStatus) {
        setValue('status', autoStatus);
      }
    }
  }, [totalPaidAmount, amount, initialData, calculateStatusFromPaidAmount, currentStatus, setValue, statusManuallyChanged]);

  // Validar que el estado sea consistente con el monto pagado total
  useEffect(() => {
    if (initialData && currentStatus && amount) {
      const totalValue = parseFloat(amount) || 0;
      
      // Si el estado es "paid" pero no se ha pagado el monto total, corregir automáticamente
      if (currentStatus === 'paid' && totalPaidAmount < totalValue) {
        const correctStatus = calculateStatusFromPaidAmount(totalPaidAmount, amount);
        setValue('status', correctStatus);
        setStatusManuallyChanged(false);
      }
    }
  }, [currentStatus, totalPaidAmount, amount, initialData, calculateStatusFromPaidAmount, setValue]);

  const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numAmount) || numAmount === null || numAmount === undefined) {
      return 'S/ 0.00';
    }
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(numAmount);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="payment-form">
      {restrictions.message && !(currentStatus === 'paid' && initialData?.status !== 'paid') && (
        <div className={`status-message status-${currentStatus}`}>
          <span className="status-icon">
            {currentStatus === 'paid' && '✅'}
            {currentStatus === 'cancelled' && '❌'}
            {currentStatus === 'partial' && '💰'}
            {currentStatus === 'overdue' && '⚠️'}
          </span>
          <span>{restrictions.message}</span>
        </div>
      )}
      
      {/* Matrícula con búsqueda */}
      <div className="form-group">
        <label htmlFor="enrollment_id">Matrícula *</label>
        {initialData ? (
          // Si es edición, mostrar solo el valor seleccionado (no editable)
          <div>
            <input
              type="text"
              value={selectedEnrollmentInfo 
                ? `${selectedEnrollmentInfo.student?.first_name || ''} ${selectedEnrollmentInfo.student?.last_name || ''} - ${selectedEnrollmentInfo.group?.name || ''} (Cuota: ${formatCurrency(selectedEnrollmentInfo.enrollment.final_monthly_fee)})`
                : 'Matrícula seleccionada'
              }
              disabled
              className="input-disabled"
            />
            <span className="info-message">
              ℹ️ No se puede cambiar la matrícula de un pago existente
            </span>
          </div>
        ) : (
          // Si es creación, mostrar selector con búsqueda
          <div className="enrollment-selector">
            <div className="enrollment-search-wrapper">
              <input
                ref={enrollmentSearchRef}
                type="text"
                placeholder="Buscar por nombre, documento o grupo..."
                value={enrollmentSearchTerm}
                onChange={(e) => {
                  setEnrollmentSearchTerm(e.target.value);
                  setShowEnrollmentDropdown(true);
                }}
                onFocus={() => setShowEnrollmentDropdown(true)}
                className={errors.enrollment_id ? 'input-error' : ''}
              />
              {showEnrollmentDropdown && (
                <div ref={enrollmentDropdownRef} className="enrollment-dropdown">
                  {filteredEnrollments.length === 0 ? (
                    <div className="dropdown-empty">
                      {enrollmentSearchTerm.trim() 
                        ? 'No se encontraron matrículas con ese criterio'
                        : 'No hay matrículas disponibles'
                      }
                    </div>
                  ) : (
                    filteredEnrollments.map((enrollment) => {
                      const student = enrollment.student || students?.find(s => s.id === enrollment.student_id);
                      const group = enrollment.group || groups?.find(g => g.id === enrollment.group_id);
                      const isSelected = enrollment.id === parseInt(selectedEnrollmentId);
                      
                      return (
                        <div
                          key={enrollment.id}
                          className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            setValue('enrollment_id', enrollment.id);
                            setEnrollmentSearchTerm(
                              student 
                                ? `${student.first_name} ${student.last_name} - ${group?.name || ''}`
                                : `Matrícula #${enrollment.id}`
                            );
                            setShowEnrollmentDropdown(false);
                          }}
                        >
                          <div className="dropdown-item-main">
                            <strong>
                              {student 
                                ? `${student.first_name} ${student.last_name}`
                                : `Estudiante #${enrollment.student_id}`
                              }
                            </strong>
                            {student?.document_number && (
                              <span className="dropdown-item-doc">DNI: {student.document_number}</span>
                            )}
                          </div>
                          <div className="dropdown-item-secondary">
                            <span>{group?.name || `Grupo #${enrollment.group_id}`}</span>
                            <span className="dropdown-item-fee">
                              {formatCurrency(enrollment.final_monthly_fee)}
                            </span>
                          </div>
                          {enrollment.status === 'pending' && (
                            <span className="enrollment-status-badge pending">Pendiente</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            <input
              type="hidden"
              {...register('enrollment_id', { 
                required: 'La matrícula es obligatoria',
                setValueAs: (value) => {
                  if (value === '' || value === null || value === undefined || isNaN(value)) {
                    return '';
                  }
                  return parseInt(value);
                }
              })}
            />
            {errors.enrollment_id && <span className="error-message">{errors.enrollment_id.message}</span>}
            {enrollmentSearchTerm && filteredEnrollments.length > 0 && (
              <span className="info-message">
                ℹ️ {filteredEnrollments.length} matrícula{filteredEnrollments.length !== 1 ? 's' : ''} encontrada{filteredEnrollments.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
        {selectedEnrollment && (
          <div className="enrollment-details">
            <strong>Información de la matrícula:</strong>
            <div>Cuota mensual: {formatCurrency(selectedEnrollment.final_monthly_fee)}</div>
            <div>Período: {
              selectedEnrollment.period_type === 'monthly' ? 'Mensual' :
              selectedEnrollment.period_type === 'quarterly' ? 'Trimestral' :
              'Anual'
            }</div>
            {selectedEnrollment.status === 'pending' && (
              <div className="enrollment-status-info">
                <span className="status-badge pending">Estado: Pendiente</span>
                <span className="info-text">Esta matrícula se activará cuando se confirme el primer pago</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fechas */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="payment_date">Fecha de Pago *</label>
          <input
            type="date"
            id="payment_date"
            {...register('payment_date', { required: 'La fecha de pago es obligatoria' })}
            className={errors.payment_date ? 'input-error' : ''}
            disabled={!!initialData && !['pending', 'partial', 'overdue'].includes(initialData.status)}
          />
          {errors.payment_date && <span className="error-message">{errors.payment_date.message}</span>}
          {initialData && ['pending', 'partial', 'overdue'].includes(initialData.status) && (
            <span className="info-message">
              ℹ️ Por defecto es hoy. Si el efectivo se recibió otro día, cambia la fecha: se guarda la que escribas.
            </span>
          )}
          {initialData && !['pending', 'partial', 'overdue'].includes(initialData.status) && (
            <span className="info-message">
              ℹ️ No se puede cambiar la fecha de pago de un pago existente
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="due_date">Fecha de Vencimiento *</label>
          <input
            type="date"
            id="due_date"
            {...register('due_date', { required: 'La fecha de vencimiento es obligatoria' })}
            className={errors.due_date ? 'input-error' : ''}
            disabled={restrictions.disabledFields.includes('due_date')}
          />
          {errors.due_date && <span className="error-message">{errors.due_date.message}</span>}
          {restrictions.disabledFields.includes('due_date') && (
            <span className="info-message">ℹ️ No se puede editar la fecha de vencimiento en este estado</span>
          )}
        </div>
      </div>

      {/* Monto Total (solo lectura) */}
      <div className="form-group">
        <label htmlFor="amount">Monto Total a Pagar (S/) *</label>
        <input
          type="number"
          id="amount"
          step="0.01"
          min="0"
          {...register('amount', { 
            required: 'El monto es obligatorio',
            valueAsNumber: true,
            min: { value: 0.01, message: 'El monto debe ser mayor a 0' }
          })}
          className={errors.amount ? 'input-error' : ''}
          placeholder="0.00"
          disabled={true}
          readOnly
        />
        {errors.amount && <span className="error-message">{errors.amount.message}</span>}
        <span className="info-message">
          ℹ️ Este monto proviene de la matrícula seleccionada y no se puede modificar
        </span>
      </div>

      {/* Pago Adelantado - Para nuevos pagos */}
      {!initialData && (
        <div className="form-group">
          <label htmlFor="paid_amount">Pago Adelantado (S/)</label>
          <input
            type="number"
            id="paid_amount"
            step="0.01"
            min="0"
            max={parseFloat(amount) || undefined}
            onWheel={(e) => {
              e.target.blur();
            }}
            {...register('paid_amount', { 
              valueAsNumber: true,
              min: { value: 0, message: 'El monto debe ser mayor o igual a 0' },
              validate: (value) => {
                const paid = parseFloat(value) || 0;
                const total = parseFloat(amount) || 0;
                if (paid > total && total > 0) {
                  return `El pago adelantado no puede ser mayor al monto total (${total.toFixed(2)})`;
                }
                return true;
              }
            })}
            className={errors.paid_amount ? 'input-error' : ''}
            placeholder="0.00"
          />
          {errors.paid_amount && <span className="error-message">{errors.paid_amount.message}</span>}
          <span className="info-message">
            ℹ️ Opcional: Ingresa un monto si el cliente desea realizar un pago adelantado
          </span>
        </div>
      )}

      {/* Monto a Pagar y Resumen - Para ediciones */}
      {initialData && (
        <div className={additionalAmountLocked ? undefined : 'payment-collect-first payment-collect-box'}>
          {!additionalAmountLocked && <h3 className="payment-collect-title">Registrar pago</h3>}
          <div className="form-group">
            <label htmlFor="additional_payment">
              {originalPaidAmount.current > 0 
                ? 'Agregar Pago Adicional (S/)' 
                : 'Monto a Pagar (S/)'}
            </label>
            <input
              type="number"
              id="additional_payment"
              step="0.01"
              min="0"
              max={maxAdditionalAmount || undefined}
              value={additionalPaymentAmount}
              onWheel={(e) => {
                e.target.blur();
              }}
              onChange={(e) => {
                const inputValue = e.target.value;
                setAdditionalPaymentAmount(inputValue);
                
                if (inputValue === '' || inputValue === null || inputValue === undefined) {
                  setValue('paid_amount', originalPaidAmount.current);
                  return;
                }
                
                const value = parseFloat(inputValue);
                
                if (!isNaN(value) && value >= 0) {
                  // Bloquear si excede el monto pendiente
                  const maxAllowed = maxAdditionalAmount || 0;
                  if (value > maxAllowed) {
                    setAdditionalPaymentAmount(maxAllowed.toString());
                    setValue('paid_amount', originalPaidAmount.current + maxAllowed);
                    return;
                  }
                  const total = originalPaidAmount.current + value;
                  setValue('paid_amount', total);
                } else {
                  setValue('paid_amount', originalPaidAmount.current);
                }
              }}
              onBlur={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '' || inputValue === null || isNaN(parseFloat(inputValue))) {
                  setAdditionalPaymentAmount('');
                  setValue('paid_amount', originalPaidAmount.current);
                } else {
                  const value = parseFloat(inputValue);
                  if (!isNaN(value) && value >= 0) {
                    // Limitar al máximo permitido
                    const maxAllowed = maxAdditionalAmount || 0;
                    const finalValue = value > maxAllowed ? maxAllowed : value;
                    setAdditionalPaymentAmount(finalValue.toString());
                    const total = originalPaidAmount.current + finalValue;
                    setValue('paid_amount', total);
                  }
                }
              }}
              className={errors.paid_amount ? 'input-error' : ''}
              placeholder={maxAdditionalAmount ? `Máximo: ${formatCurrency(maxAdditionalAmount)}` : "0.00"}
              disabled={additionalAmountLocked}
            />
            {errors.paid_amount && <span className="error-message">{errors.paid_amount.message}</span>}
            {additionalAmountLocked && (
              <span className="info-message">ℹ️ No se puede editar el monto pagado en este estado</span>
            )}
            {!additionalAmountLocked && (
              <span className="info-message">
                {originalPaidAmount.current > 0 
                  ? `ℹ️ Ingresa el monto adicional a pagar. Pendiente: ${formatCurrency(remainingAmount)}`
                  : 'ℹ️ Ingresa el monto que se está pagando en esta transacción'
                }
              </span>
            )}
          </div>

          {/* Resumen compacto */}
          {amount && (
            <div className="payment-summary-compact">
              <div className="summary-row-compact">
                <span className="summary-label-compact">Total:</span>
                <span className="summary-value-compact">{formatCurrency(parseFloat(amount) || 0)}</span>
              </div>
              <div className="summary-row-compact">
                <span className="summary-label-compact">Pagado:</span>
                <span className="summary-value-compact paid">{formatCurrency(originalPaidAmount.current)}</span>
              </div>
              {parseFloat(additionalPaymentAmount) > 0 && (
                <div className="summary-row-compact highlight">
                  <span className="summary-label-compact">Nuevo:</span>
                  <span className="summary-value-compact new-payment">+ {formatCurrency(parseFloat(additionalPaymentAmount) || 0)}</span>
                </div>
              )}
              {/* Historial de Pagos Parciales: entre Pagado y Total Pagado */}
              {initialData && initialData.details && initialData.details.length > 0 && (
                <div className="partial-payments-history partial-payments-inline">
                  <h4 className="partial-payments-title">📋 Historial de Pagos Parciales</h4>
                  <table className="partial-payments-table">
                    <thead>
                      <tr>
                        <th>Fecha Pago</th>
                        <th>Importe</th>
                        <th>Método Pago</th>
                      </tr>
                    </thead>
                    <tbody>
                      {initialData.details.map((detail, index) => (
                        <tr key={detail.id || index}>
                          <td>{formatDateDisplay(detail.payment_date)}</td>
                          <td className="amount-cell">{formatCurrency(parseFloat(detail.amount))}</td>
                          <td>{
                            detail.payment_method === 'cash' ? '💵 Efectivo' :
                            detail.payment_method === 'card' ? '💳 Tarjeta' :
                            detail.payment_method === 'transfer' ? '🏦 Transferencia' :
                            detail.payment_method === 'yape' ? '📱 Yape' :
                            detail.payment_method === 'plin' ? '📱 Plin' :
                            detail.payment_method === 'other' ? '📝 Otro' :
                            detail.payment_method || '—'
                          }</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="summary-row-compact total">
                <span className="summary-label-compact"><strong>Total Pagado:</strong></span>
                <span className="summary-value-compact"><strong>{formatCurrency(totalPaidAmount)}</strong></span>
              </div>
              <div className="summary-row-compact remaining">
                <span className="summary-label-compact"><strong>Pendiente:</strong></span>
                <span className={`summary-value-compact ${remainingAmount > 0 ? 'pending' : 'paid'}`}>
                  <strong>{formatCurrency(remainingAmount)}</strong>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Método de Pago */}
      <div className={`form-group${initialData && !additionalAmountLocked ? ' payment-collect-first' : ''}`}>
        <label htmlFor="payment_method">Método de Pago</label>
        <select
          id="payment_method"
          {...register('payment_method')}
          disabled={restrictions.disabledFields.includes('payment_method')}
        >
          <option value="">Seleccionar método...</option>
          <option value="cash">💵 Efectivo</option>
          <option value="card">💳 Tarjeta</option>
          <option value="transfer">🏦 Transferencia</option>
          <option value="yape">📱 Yape</option>
          <option value="plin">📱 Plin</option>
          <option value="other">📝 Otro</option>
        </select>
        {restrictions.disabledFields.includes('payment_method') && (
          <span className="info-message">ℹ️ No se puede editar el método de pago en este estado</span>
        )}
      </div>

      {/* Referencias */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="reference_number">Número de Referencia</label>
          <input
            type="text"
            id="reference_number"
            {...register('reference_number')}
            placeholder="Número de referencia del pago"
          />
        </div>

        {initialData && (
          <div className="form-group">
            <label htmlFor="invoice_number">Número de Factura</label>
            <input
              type="text"
              id="invoice_number"
              {...register('invoice_number')}
              placeholder="Número de factura"
            />
          </div>
        )}
      </div>

      {/* Estado - Solo para ediciones */}
      {initialData && (
        <div className="form-group">
          <label htmlFor="status">Estado</label>
          <select
            id="status"
            {...register('status', {
              onChange: (e) => {
                const selectedStatus = e.target.value;
                const paidValue = parseFloat(paidAmount) || 0;
                const totalValue = parseFloat(amount) || 0;
                
                if (selectedStatus === 'paid' && paidValue < totalValue) {
                  const correctStatus = calculateStatusFromPaidAmount(paidAmount, amount);
                  setValue('status', correctStatus);
                  setStatusManuallyChanged(false);
                  return;
                }
                
                setStatusManuallyChanged(true);
              },
              validate: (value) => {
                if (!initialData) return true;
                const paidValue = parseFloat(paidAmount) || 0;
                const totalValue = parseFloat(amount) || 0;
                
                if (value === 'paid' && paidValue < totalValue) {
                  return 'No se puede marcar como pagado si el monto pagado es menor al monto total';
                }
                
                return true;
              }
            })}
            disabled={!restrictions.statusChangeable || restrictions.disabledFields.includes('status')}
          >
            <option value="pending">⏳ Pendiente</option>
            <option value="paid">✅ Pagado</option>
            <option value="partial">💰 Parcial</option>
            <option value="overdue">⚠️ Vencido</option>
            <option value="cancelled">❌ Cancelado</option>
          </select>
          {errors.status && <span className="error-message">{errors.status.message}</span>}
          {(!restrictions.statusChangeable || restrictions.disabledFields.includes('status')) && (
            <span className="info-message">
              ℹ️ {currentStatus === 'cancelled' 
                ? 'Solo administradores pueden cambiar el estado de un pago cancelado'
                : 'No se puede cambiar el estado en este momento'}
            </span>
          )}
          {restrictions.statusChangeable && !restrictions.disabledFields.includes('status') && (
            <span className="info-message">
              ℹ️ El estado se actualiza automáticamente según el monto pagado. No puedes marcar como "Pagado" si el monto pagado es menor al total.
            </span>
          )}
        </div>
      )}

      {/* Notas */}
      <div className="form-group">
        <label htmlFor="notes">Notas</label>
        <textarea
          id="notes"
          {...register('notes')}
          rows="3"
          placeholder="Notas adicionales sobre el pago..."
        />
      </div>

      {/* Botones */}
      <div className="modal-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Guardando...' : initialData ? (additionalAmountLocked ? 'Actualizar' : 'Registrar pago') : 'Crear'}
        </button>
      </div>
    </form>
  );
};

export default PaymentForm;
